import { Button, Tooltip } from "@fluentui/react-components";
import {
  ArrowDownloadRegular,
  DeleteRegular,
  DocumentAdd16Regular,
} from "@fluentui/react-icons";
import { getDownloadSignedUrl } from "api";
import { ALLOWED_FILE_EXTENSIONS } from "common/constants";
import { FileWithId } from "models/FileWithId";
import { IFile } from "models/IFile";
import React, { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { showToast } from "../../redux/toastActions";
import { resolveAsset } from "../../common/fileIcons";
import { useSharedFileCardStyles } from "theme/sharedStyles";
import { IAttachmentsHandler } from "./AttachmentsHandler.types";

export const AttachmentsHandler = <
  T extends { name: string; attachments?: IFile[]; description?: string },
>(
  props: IAttachmentsHandler<T>,
) => {
  const fileCardStyles = useSharedFileCardStyles();
  const fileInputRef: React.RefObject<HTMLInputElement> =
    useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();

  const { entity, downloadFiles } = props;
  const currentAttachments = useSelector(
    (state: RootState) => state.currentAttachments.value,
  );

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const files: FileList | null = event.target.files;

    if (files) {
      const newFiles: FileWithId[] = Array.from(files)
        .filter((file) => {
          const extension = file.name.split(".").pop()?.toLowerCase();
          if (
            extension &&
            ALLOWED_FILE_EXTENSIONS.map((ext) => ext.toLowerCase()).includes(
              extension,
            )
          ) {
            return true;
          } else {
            dispatch(
              showToast(
                `The file "${file.name}" has an unsupported file extension.`,
                "warning",
              ),
            );
            return false;
          }
        })
        .map((file) => ({
          file: file,
        }));

      const existingFilenames: string[] = currentAttachments.map(
        (attachment: FileWithId) => attachment.file.name,
      );

      const duplicates: FileWithId[] = newFiles.filter(
        (file: FileWithId): boolean =>
          existingFilenames.includes(file.file.name),
      );

      if (duplicates.length > 0) {
        dispatch(
          showToast(
            `The following files already exist: ${duplicates.map((file: FileWithId) => file.file.name).join(", ")}`,
            "warning",
          ),
        );
        return;
      }

      const combinedFiles: FileWithId[] = [...currentAttachments, ...newFiles];

      if (props.onFilesChange) {
        props.onFilesChange(currentAttachments, combinedFiles);
      }
    }
  };

  const deselectFile = (file: IFile) => (): void => {
    if (props.entity.attachments) {
      if (props.onFilesChange) {
        const uniqueFilesMap = new Map<string, FileWithId>();

        props.entity.attachments.forEach((att) => {
          if (!uniqueFilesMap.has(att.filename)) {
            uniqueFilesMap.set(att.filename, {
              id: att.id,
              file: new File([], att.filename),
            });
          }
        });

        const convertedFiles: FileWithId[] = Array.from(
          uniqueFilesMap.values(),
        );

        const remainingFiles: FileWithId[] = convertedFiles.filter(
          (attachment: FileWithId) => attachment.file.name !== file.filename,
        );

        props.onFilesChange(convertedFiles, remainingFiles, true);
      }
    }
  };

  const selectFileFromOS = (): void => fileInputRef.current?.click();

  const handleDownloadFile = async (file: IFile): Promise<void> => {
    if (downloadFiles && file.url) {
      const fileWithUrl: any = await getDownloadSignedUrl({
        filename: file.url,
      });
      window.open(fileWithUrl.sas, "_blank");
    }
  };

  return (
    <React.Fragment>
      {!downloadFiles &&
        entity.attachments &&
        entity.attachments.length > 0 &&
        entity.attachments.map(
          (file: IFile): JSX.Element => (
            <div
              className={fileCardStyles.fileCard}
              role="listitem"
              key={file.filename}
            >
              <div className={fileCardStyles.fileIcon}>
                <img
                  src={resolveAsset(file.filename, file)}
                  alt={file.filename}
                  width="28"
                  height="28"
                />
              </div>
              <Tooltip content={file.filename} relationship="description">
                <div className={fileCardStyles.fileName}>{file.filename}</div>
              </Tooltip>
              {props.takesFilesFromEntity === false ||
              props.takesFilesFromEntity === undefined ? (
                <div className={fileCardStyles.fileActions}>
                  <Button
                    appearance="transparent"
                    icon={<DeleteRegular />}
                    onClick={deselectFile(file)}
                    className={fileCardStyles.fileDeleteButton}
                  />
                </div>
              ) : null}
            </div>
          ),
        )}

      {downloadFiles &&
        entity.attachments &&
        entity.attachments.length > 0 &&
        entity.attachments.map(
          (file: IFile): JSX.Element => (
            <div
              className={fileCardStyles.fileCard}
              role="listitem"
              key={file.filename}
            >
              <div className={fileCardStyles.fileIcon}>
                <img
                  src={resolveAsset(file.filename, file)}
                  alt={file.filename}
                  width="28"
                  height="28"
                />
              </div>
              <Tooltip
                content={file.filename.split("/").pop() ?? file.filename}
                relationship="description"
              >
                <div className={fileCardStyles.fileName}>
                  {file.filename.split("/").pop()}
                </div>
              </Tooltip>
              <div className={fileCardStyles.fileActions}>
                <Tooltip content="Download" relationship="label">
                  <Button
                    appearance="transparent"
                    icon={<ArrowDownloadRegular />}
                    onClick={() => handleDownloadFile(file)}
                    className={fileCardStyles.fileDownloadButton}
                  />
                </Tooltip>
                {(props.takesFilesFromEntity === false ||
                  props.takesFilesFromEntity === undefined) && (
                  <Tooltip content="Delete" relationship="label">
                    <Button
                      appearance="transparent"
                      icon={<DeleteRegular />}
                      onClick={deselectFile(file)}
                      className={fileCardStyles.fileDeleteButton}
                    />
                  </Tooltip>
                )}
              </div>
            </div>
          ),
        )}

      {!props.hideAddButton &&
      (props.takesFilesFromEntity === false ||
        props.takesFilesFromEntity === undefined) ? (
        <Button
          icon={<DocumentAdd16Regular />}
          size="small"
          onClick={selectFileFromOS}
        >
          Add Document
        </Button>
      ) : undefined}
      <input
        onChange={handleFileInputChange}
        ref={fileInputRef}
        type="file"
        hidden
        multiple
      />
    </React.Fragment>
  );
};
